import { useEffect, useState } from "react";
import { MapPin, Ticket } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import { api } from "@/lib/api";

const FLYER = "/api/files/millzz/tour/flyer.jpg";

export function formatShowDate(d) {
  const dt = new Date(`${d}T00:00:00`);
  return {
    month: dt.toLocaleString("en-US", { month: "short" }).toUpperCase(),
    day: String(dt.getDate()).padStart(2, "0"),
    year: dt.getFullYear(),
  };
}

export default function Tour() {
  const [shows, setShows] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/shows").then((r) => r.data).catch(() => []),
      api.get("/bookings/confirmed").then((r) => r.data).catch(() => []),
    ]).then(([shows, locked]) => {
      const merged = [
        ...shows,
        ...locked.map((b) => ({
          id: `booking-${b.id}`,
          date: b.date,
          city: (b.city || "").toUpperCase(),
          venue: b.name,
          status: "LOCKED IN",
          ticket_url: "",
        })),
      ].sort((a, b) => a.date.localeCompare(b.date));
      setShows(merged);
    });
    api.get("/settings/live").then((r) => setLive(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="tour-page" className="pb-40 pt-36">
      <div className="mx-auto max-w-7xl px-6">
        <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">MIKE MILLZZ LIVE WITH JODECI — 2026</p>
        <h1 data-testid="tour-title" className="font-anton leading-[0.9]">
          <MaskedLine className="text-5xl text-white md:text-8xl" delay={0.1}>FOREVER MY</MaskedLine>
          <MaskedLine className="text-outline text-5xl md:text-8xl" delay={0.25}>LADY TOUR</MaskedLine>
        </h1>
        <p className="mt-8 max-w-lg text-white/60">
          Millzz joins JODECI on the Forever My Lady 35th Anniversary Tour — 38 cities, one legendary run. Official ticket links on every date.
        </p>
      </div>

      <EditorialMarquee className="mt-16" speed={40} items={["FOREVER MY LADY TOUR", "JODECI × MIKE MILLZZ", "35TH ANNIVERSARY"]} />

      {live?.enabled && live?.url && (
        <div className="mx-auto mt-16 max-w-7xl px-6">
          <Reveal>
            <div data-testid="live-concert" id="live-concert" className="border border-primary/40 bg-black p-4 md:p-6">
              <div className="mb-4 flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping bg-primary opacity-75" />
                  <span className="relative inline-flex h-3 w-3 bg-primary" />
                </span>
                <p className="font-mono text-xs tracking-[0.4em] text-primary">LIVE CONCERT — ON NOW</p>
              </div>
              <video
                data-testid="live-video"
                src={live.url}
                controls
                autoPlay
                muted
                loop
                playsInline
                poster="https://customer-assets-v7afamib.emergentagent.net/job_soundwave-shop-10/artifacts/3p2bphn4_IMG_3187.PNG"
                className="w-full"
              />
              <p data-testid="live-video-title" className="mt-4 font-oswald text-lg font-bold tracking-wider text-white">{live.title}</p>
            </div>
          </Reveal>
        </div>
      )}

      <div className="mx-auto mt-20 grid max-w-7xl gap-12 px-6 lg:grid-cols-[360px_1fr]">
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <div data-testid="tour-flyer" className="border border-primary/40 bg-black p-3">
            <img src={FLYER} alt="Jodeci Forever My Lady Tour official flyer" className="w-full" />
            <p className="mt-3 flex justify-between font-mono text-[10px] tracking-[0.3em] text-white/50">
              <span>OFFICIAL TOUR FLYER</span>
              <span className="text-primary">35TH ANNIVERSARY</span>
            </p>
          </div>
        </Reveal>
        <div data-testid="tour-show-list">
        {shows.map((s, i) => {
          const d = formatShowDate(s.date);
          const soldOut = s.status === "SOLD OUT";
          return (
            <Reveal key={s.id} delay={i * 0.06}>
              <div
                data-testid={`tour-show-${s.id}`}
                className="group flex flex-col gap-6 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.03] md:flex-row md:flex-wrap md:items-center"
              >
                <div className="flex w-28 shrink-0 flex-col border border-primary/30 p-4 text-center">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-primary">{d.month}</span>
                  <span className="font-anton text-4xl text-white">{d.day}</span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">{d.year}</span>
                  {s.time && <span className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/60">{s.time}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-anton text-3xl text-white md:text-4xl">{s.city}</p>
                  <p className="mt-1 flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-white/50">
                    <MapPin size={12} className="text-primary" /> {s.venue}
                  </p>
                </div>
                <span
                  data-testid={`tour-status-${s.id}`}
                  className={`w-fit px-3 py-1 font-mono text-[10px] tracking-[0.25em] ${
                    s.status === "LIVE NOW" ? "animate-pulse bg-primary font-bold text-black" : soldOut ? "border border-white/25 text-white/50" : "bg-primary/15 text-primary"
                  }`}
                >
                  {s.status}
                </span>
                {s.status === "LIVE NOW" ? (
                  <a
                    href="#live-concert"
                    data-testid={`tour-watch-${s.id}`}
                    className="flex w-fit animate-pulse items-center gap-2 bg-primary px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] text-black"
                  >
                    <Ticket size={14} /> WATCH LIVE
                  </a>
                ) : soldOut ? (
                  <span className="w-fit cursor-not-allowed border border-white/15 px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] text-white/30">
                    SOLD OUT
                  </span>
                ) : s.ticket_url ? (
                  <a
                    href={s.ticket_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`tour-tickets-${s.id}`}
                    className="flex w-fit items-center gap-2 bg-primary px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[4px_4px_0px_#fff]"
                  >
                    <Ticket size={14} /> TICKETS
                  </a>
                ) : (
                  <button
                    data-testid={`tour-tickets-${s.id}`}
                    onClick={() => toast.info("Tickets drop soon — join the fan list to get first access.")}
                    className="flex w-fit items-center gap-2 border border-white/30 px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
                  >
                    <Ticket size={14} /> NOTIFY ME
                  </button>
                )}
                {s.photos?.length > 0 && (
                  <div data-testid={`tour-gallery-${s.id}`} className="flex w-full basis-full snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
                    {s.photos.map((ph, j) => (
                      <img key={j} src={ph} alt={`${s.city} show photo ${j + 1}`}
                        className="h-28 w-44 shrink-0 snap-center border border-white/10 object-cover" />
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          );
        })}
        {shows.length === 0 && (
          <p data-testid="tour-empty" className="py-20 text-center font-mono text-xs tracking-[0.3em] text-white/40">
            NEW DATES LOADING...
          </p>
        )}
        </div>
      </div>
    </main>
  );
}
